import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getCourseById, getCourseChapters } from '../services/courses';
import { buildPlaylistPageState } from '../utils/playlistMappers';

export function usePlaylistPage(courseId) {
  const [searchParams, setSearchParams] = useSearchParams();
  const lessonFromUrl = searchParams.get('lesson');
  const syncedLessonFromUrlRef = useRef(null);
  const [course, setCourse] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [activeChapterId, setActiveChapterId] = useState(null);
  const [activeLessonId, setActiveLessonId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setActiveChapterId(null);
    setActiveLessonId(null);
  }, [courseId]);

  const fetchPlaylist = useCallback(async () => {
    if (!courseId) {
      setCourse(null);
      setChapters([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [courseRes, chaptersRes] = await Promise.all([
        getCourseById(courseId),
        getCourseChapters(courseId),
      ]);

      const rawCourse = courseRes?.data;
      const rawChapters = chaptersRes?.data?.chapters || [];

      setCourse(rawCourse);
      setChapters(rawChapters);

      const firstChapter = rawChapters[0];
      const firstLesson = firstChapter?.lessons?.[0];
      setActiveChapterId((current) => current || firstChapter?.id || null);
      setActiveLessonId((current) => current || firstLesson?.id || null);
    } catch (e) {
      setCourse(null);
      setChapters([]);
      setError(e?.message || 'Failed to load playlist');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    fetchPlaylist();
  }, [fetchPlaylist]);

  useEffect(() => {
    syncedLessonFromUrlRef.current = null;
  }, [lessonFromUrl, courseId]);

  useEffect(() => {
    if (!lessonFromUrl || !chapters.length) return;
    if (syncedLessonFromUrlRef.current === lessonFromUrl) return;

    const lessonId = Number(lessonFromUrl);
    if (!Number.isFinite(lessonId)) return;

    for (const chapter of chapters) {
      const lesson = chapter.lessons?.find((item) => Number(item.id) === lessonId);
      if (lesson) {
        syncedLessonFromUrlRef.current = lessonFromUrl;
        setActiveChapterId(chapter.id);
        setActiveLessonId(lessonId);
        return;
      }
    }
  }, [lessonFromUrl, chapters]);

  const selectChapter = useCallback(
    (chapterId) => {
      setActiveChapterId(chapterId);
      const chapter = chapters.find((item) => item.id === chapterId);
      const firstLesson = chapter?.lessons?.[0];
      setActiveLessonId(firstLesson?.id ?? null);
    },
    [chapters]
  );

  const selectLesson = useCallback(
    (lessonId) => {
      if (!lessonId) return;

      setActiveLessonId(lessonId);
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set('lesson', String(lessonId));
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const markLessonProgress = useCallback((lessonId, update = {}) => {
    if (!lessonId) return;

    setChapters((currentChapters) =>
      currentChapters.map((chapter) => ({
        ...chapter,
        lessons: (chapter.lessons || []).map((lesson) => {
          if (Number(lesson.id) !== Number(lessonId)) return lesson;

          return {
            ...lesson,
            ...(update.watchedSeconds != null
              ? { watched_seconds: update.watchedSeconds }
              : {}),
            ...(update.videoDurationSeconds != null
              ? { video_duration_seconds: update.videoDurationSeconds }
              : {}),
            ...(update.completed
              ? { is_played_by_current_user: true, completed: true }
              : {}),
          };
        }),
      }))
    );
  }, []);

  const markLessonComplete = useCallback(
    (lessonId) => {
      markLessonProgress(lessonId, { completed: true });
    },
    [markLessonProgress]
  );

  const markLessonDownloaded = useCallback((lessonId) => {
    if (!lessonId) return;

    setChapters((currentChapters) =>
      currentChapters.map((chapter) => ({
        ...chapter,
        lessons: (chapter.lessons || []).map((lesson) =>
          lesson.id === lessonId ? { ...lesson, is_downloaded: true } : lesson
        ),
      }))
    );
  }, []);

  const pageState = useMemo(
    () =>
      buildPlaylistPageState({
        course,
        chapters,
        activeChapterId,
        activeLessonId,
      }),
    [course, chapters, activeChapterId, activeLessonId]
  );

  return {
    ...pageState,
    courseId: course?.id ?? null,
    loading,
    error,
    selectChapter,
    selectLesson,
    markLessonProgress,
    markLessonComplete,
    markLessonDownloaded,
    refetch: fetchPlaylist,
  };
}
